export type QuestionStepId = 'q1' | 'q2' | 'q3' | 'q4'
export type FeedbackStepId = 'feedback-destination' | 'feedback-cash' | 'feedback-success'
export type SurveyStep = QuestionStepId | FeedbackStepId

export type QuestionOption = {
  id: string
  label: string
  analyticsValue?: string
  /** When true, cannot be combined with non-exclusive options (mutually exclusive group). */
  exclusive?: boolean
  /** Overrides `defaultNextStep` on the question when this option is selected (radio) or is the exclusive selection (checkbox). */
  nextStep?: SurveyStep
}

export type QuestionStepConfig = {
  id: QuestionStepId
  stepNumber: 1 | 2 | 3 | 4
  headerTitle: string
  title: string
  continueLabel: 'Continuar' | 'Enviar'
  options: QuestionOption[]
  type?: 'radio' | 'checkbox'
  defaultNextStep: SurveyStep
}

export type FeedbackStepConfig = {
  title: string
  description: string
  stepNumber: number
  isSuccess: boolean
}

export function resolveNextStep(question: QuestionStepConfig, selectedIds: string[]): SurveyStep {
  if (question.type === 'checkbox') {
    const exclusiveSelected = question.options.find(
      (o) => o.exclusive && selectedIds.includes(o.id),
    )
    return exclusiveSelected?.nextStep ?? question.defaultNextStep
  }

  const selected = question.options.find((o) => selectedIds.includes(o.id))
  return selected?.nextStep ?? question.defaultNextStep
}

export const QUESTION_STEPS: Record<QuestionStepId, QuestionStepConfig> = {
  q1: {
    id: 'q1',
    stepNumber: 1,
    headerTitle: 'Queremos saber mais',
    title: 'Você vai para a Copa de 2026?',
    continueLabel: 'Continuar',
    type: 'checkbox',
    defaultNextStep: 'q2',
    options: [
      { id: 'usa', label: 'Sim, para os EUA', analyticsValue: 'EUA' },
      { id: 'canada', label: 'Sim, para o Canadá', analyticsValue: 'Canadá' },
      { id: 'mexico', label: 'Sim, para o México', analyticsValue: 'México' },
      {
        id: 'undecided',
        label: 'Ainda não decidi',
        analyticsValue: 'Ainda não decidi',
        exclusive: true,
        nextStep: 'feedback-destination',
      },
      {
        id: 'no',
        label: 'Não',
        analyticsValue: 'Não',
        exclusive: true,
        nextStep: 'feedback-destination',
      },
    ],
  },
  q2: {
    id: 'q2',
    stepNumber: 2,
    headerTitle: 'Queremos saber mais',
    title: 'Quanto dinheiro em espécie você planeja levar no total?',
    continueLabel: 'Continuar',
    defaultNextStep: 'q3',
    options: [
      { id: 'up_to_150', label: 'Até US$ 150' },
      { id: 'between_150_500', label: 'Entre US$ 150 e US$ 500' },
      { id: 'between_501_1000', label: 'Entre US$ 501 e US$ 1.000' },
      { id: 'more_than_1001', label: 'Mais que US$ 1.001' },
      { id: 'no_cash', label: 'Não pretendo levar dinheiro físico', nextStep: 'feedback-cash' },
    ],
  },
  q3: {
    id: 'q3',
    stepNumber: 3,
    headerTitle: 'Queremos saber mais',
    title: 'Como prefere pegar esse dinheiro?',
    continueLabel: 'Continuar',
    defaultNextStep: 'q4',
    options: [
      { id: 'vip_lounge', label: 'Salas VIP do Inter (CNF, GRU, FOR, CWB)' },
      { id: 'delivery', label: 'Por serviço de entrega' },
    ],
  },
  q4: {
    id: 'q4',
    stepNumber: 4,
    headerTitle: 'Queremos entender você',
    title: 'Para quando você precisa do dinheiro?',
    continueLabel: 'Enviar',
    defaultNextStep: 'feedback-success',
    options: [
      { id: 'april', label: 'Abril' },
      { id: 'may', label: 'Maio' },
      { id: 'june', label: 'Junho' },
      { id: 'july', label: 'Julho' },
    ],
  },
}

export const FEEDBACK_CONTENT: Record<FeedbackStepId, FeedbackStepConfig> = {
  'feedback-destination': {
    title: 'Agradecemos pela participação!',
    description:
      'No momento, estamos buscando pessoas que já têm destino definido para a Copa de 2026.',
    stepNumber: 1,
    isSuccess: false,
  },
  'feedback-cash': {
    title: 'Agradecemos sua participação!',
    description:
      'No momento, estamos buscando pessoas que têm interesse no dinheiro em espécie para a Copa de 2026.',
    stepNumber: 2,
    isSuccess: false,
  },
  'feedback-success': {
    title: 'Agradecemos sua participação!',
    description:
      'Ainda não é possível receber dinheiro em espécie pela Conta Global. Se precisar, você pode sacar dinheiro via ATM em qualquer lugar do mundo usando seu Cartão Global Inter.',
    stepNumber: 5,
    isSuccess: true,
  },
}

export function getAnalyticsStep(currentStep: SurveyStep): number {
  const question = QUESTION_STEPS[currentStep as QuestionStepId]
  if (question) {
    return question.stepNumber
  }
  return FEEDBACK_CONTENT[currentStep as FeedbackStepId].stepNumber
}
